import { PermissionEnum } from '@api/enum';
import { ICamera } from '@api/models/camera.model';
import useDialog from '@hooks/use-dialog';
import { Button } from '@hyperflake/react-ui-library';
import { GlobeIcon } from '@icon/index';
import { IconButton } from '@library/buttons';
import CameraMapPanel from 'modules/cameras/features/CameraMapPanel/CameraMapPanel';
import { useCameraListData } from 'modules/cameras/hooks/useCameraListData';
import useProjectDefaultView from 'modules/projects/hooks/useProjectDefaultView';
import { EmptyDataView, PageHeading, PageTemplate } from 'modules/shared/features/content';
import { BlockLoader } from 'modules/shared/features/loaders';
import useAuthPermission from 'modules/shared/hooks/use-auth-permission';
import { useMemo } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { twJoin } from 'tailwind-merge';
import AddCameraDrawer from '../AddCameraDrawer/AddCameraDrawer';
import CameraCard from '../CameraCard/CameraCard';
import CameraConcludeCard from '../CameraConcludeCard/CameraConcludeCard';
import CameraConcludeConfirmationDrawer from '../CameraConcludeConfirmationDrawer/CameraConcludeConfirmationDrawer';
import CameraDeleteConfirmationDrawer from '../CameraDeleteConfirmationDrawer/CameraDeleteConfirmationDrawer';
import CameraDrawerLayout from '../CameraDrawerLayout/CameraDrawerLayout';
import CameraRestoreConfirmationDrawer from '../CameraRestoreConfirmationDrawer/CameraRestoreConfirmationDrawer';

const CameraList = () => {
    const navigate = useNavigate();
    const { clientId, projectId } = useParams();
    const addDrawerDialog = useDialog();
    const editDrawerDialog = useDialog<string>();
    const deleteDrawerDialog = useDialog<ICamera>();
    const concludeDrawerDialog = useDialog<ICamera>();
    const restoreDrawerDialog = useDialog<ICamera>();
    const { hasPermission } = useAuthPermission();

    const cameraListData = useCameraListData(clientId, projectId);

    const { isDefaultView, isMapView, toggleView } = useProjectDefaultView();

    const cameras = useMemo(() => cameraListData.cameraList ?? [], [cameraListData.cameraList]);

    // Handle camera selection
    // const handleCameraSelect = useCallback((camera: ICamera) => {
    //     setSelectedCamera(camera);
    //     // Set initial values from camera data
    //     setCameraAngle(Number(camera.angle) || 45);
    //     setCameraRange(Number(camera.distance) || 100);
    //     setCameraFOV(Number(camera.fieldOfView) || 90);
    // }, []);

    // const handleCloseControls = useCallback(() => {
    //     setSelectedCamera(null);
    // }, []);

    // Check if there are unsaved changes

    // const hasUnsavedChanges = useMemo(() => {
    //     if (!camera) return false;
    //     return (
    //         cameraAngle !== (+camera?.angle || 45) ||
    //         cameraRange !== (+camera?.distance || 100) ||
    //         cameraFOV !== (+camera?.fieldOfView || 90) ||
    //         isEditingLocation
    //     );
    // }, [camera, cameraAngle, cameraRange, cameraFOV, isEditingLocation]);

    if (cameraListData.isLoading) return <BlockLoader />;

    return (
        <PageTemplate>
            <div className="flex justify-between items-center">
                <PageHeading>TimeLapse Cameras</PageHeading>
                <div className="flex items-center gap-3">
                    {isDefaultView && hasPermission(PermissionEnum.TIMELAPSE_CAMERA_CREATE) && (
                        <Link to={`/clients/${clientId}/projects/${projectId}/cameras/add-camera`}>
                            <Button>Add Camera</Button>
                        </Link>
                    )}

                    <IconButton
                        className={twJoin(
                            'relative text-white transition-all duration-200',
                            !isMapView
                                ? 'bg-primary border-primary shadow-lg '
                                : 'bg-primary border-primary hover:shadow-md ring-4 ring-primary/50 animate-pulse'
                        )}
                        onClick={() => toggleView()}
                    >
                        <GlobeIcon />
                    </IconButton>
                </div>
            </div>

            {isDefaultView && (
                <div>
                    <h5 className="mt-4">Active Cameras</h5>
                    <div className="grid grid-cols-12 gap-6 mt-6 empty:hidden">
                        {cameras
                            .filter((camera) => camera.status !== 'CONCLUDED' && camera.status !== 'CONCLUDING')
                            .map((camera) => (
                                <Link
                                    key={camera._id}
                                    to={`/clients/${clientId}/projects/${projectId}/cameras/${camera._id}`}
                                    className="col-span-12 sm:col-span-6 lg:col-span-3"
                                >
                                    <CameraCard
                                        camera={camera}
                                        onEditClick={() =>
                                            navigate(
                                                `/clients/${clientId}/projects/${projectId}/cameras/${camera._id}/edit-camera`
                                            )
                                        }
                                        onLocationClick={() =>
                                            navigate(
                                                `/clients/${clientId}/projects/${projectId}/cameras/${camera._id}/edit-camera`,
                                                { state: { focus: 'map-preview' } }
                                            )
                                        }
                                        onBimClick={() =>
                                            navigate(
                                                `/clients/${clientId}/projects/${projectId}/cameras/${camera._id}/bim-viewer`
                                            )
                                        }
                                        onDeleteClick={(c) => deleteDrawerDialog.show(c)}
                                        onConcludeClick={(c) => concludeDrawerDialog.show(c)}
                                        hasDeletePermission={hasPermission(PermissionEnum.TIMELAPSE_CAMERA_DELETE)}
                                    />
                                </Link>
                            ))}
                    </div>

                    {cameras.length > 0 && (
                        <div>
                            <hr className="mt-4 mb-4 border-b-2" />
                            <div className="mt-4">
                                <h5>Conclude Cameras</h5>
                                <div className="grid grid-cols-12 gap-6 mt-6 empty:hidden opacity-70">
                                    {cameras
                                        .filter(
                                            (camera) =>
                                                camera.status === 'CONCLUDED' ||
                                                camera.status === 'CONCLUDING' ||
                                                camera.status === 'RESTORING'
                                        )
                                        .map((camera) => (
                                            <div className="col-span-12 sm:col-span-6 lg:col-span-3" key={camera._id}>
                                                <CameraConcludeCard
                                                    camera={camera}
                                                    onRestoreClick={() => restoreDrawerDialog.show(camera)}
                                                />
                                            </div>
                                        ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {cameras.length === 0 && <EmptyDataView message="No cameras found" />}
                </div>
            )}

            {isMapView && (
                <CameraMapPanel
                    cameras={cameraListData.cameraList ?? []}
                    loadingMessage="Loading Camera Map"
                    onSaveCamera={(cameraId: string, updated: ICamera) =>
                        cameraListData.updateCamera(cameraId, updated)
                    }
                    // onSaveCamera={async (cameraId: string, payload: ICamera) => {
                    //     await cameraListData.updateCamera(cameraId, payload as any);
                    // }}
                />
            )}

            <AddCameraDrawer
                open={addDrawerDialog.isOpen}
                onClose={addDrawerDialog.hide}
                onCameraAdd={cameraListData.addCamera}
            />

            <CameraDrawerLayout
                open={editDrawerDialog.isOpen}
                onClose={editDrawerDialog.hide}
                cameraId={editDrawerDialog.data}
                onCameraUpdate={(camera) => cameraListData.updateCamera(camera._id, camera)}
            />

            <CameraDeleteConfirmationDrawer
                open={deleteDrawerDialog.isOpen}
                onClose={deleteDrawerDialog.hide}
                camera={deleteDrawerDialog.data}
            />
            <CameraConcludeConfirmationDrawer
                open={concludeDrawerDialog.isOpen}
                onClose={concludeDrawerDialog.hide}
                camera={concludeDrawerDialog.data}
                onCameraUpdate={(camera) => cameraListData.updateCamera(camera._id, camera)}
            />
            <CameraRestoreConfirmationDrawer
                open={restoreDrawerDialog.isOpen}
                onClose={restoreDrawerDialog.hide}
                camera={restoreDrawerDialog.data}
                onCameraUpdate={(camera) => cameraListData.updateCamera(camera._id, camera)}
            />
        </PageTemplate>
    );
};

export default CameraList;
